import React from 'react';
import {
  Page,
  Layout,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Badge,
  Banner,
  SkeletonBodyText,
  SkeletonDisplayText,
  ProgressBar,
  Box,
  Checkbox,
  Divider,
  Button,
} from '@shopify/polaris';
import { useBenchmarks, useBenchmarkOptOut, useUpdateBenchmarkOptOut } from '../hooks/useBenchmark';
import { PageEntrance } from '../components/ui';
import { SEMANTIC_COLORS } from '../theme/colors';
import { useNavigate } from 'react-router-dom';
import './BenchmarkPage.css';

/**
 * Phase 3 — F3.2: AI Readiness Report.
 *
 * Reframed from "competitive benchmarks" to an AI-readiness gap analysis.
 * The comparison target is the AI-Ready threshold (75), NOT other merchants.
 * This creates urgency around the AI discovery opportunity rather than
 * complacency from low industry averages.
 */

/** Score at or above which a store is considered "AI-Ready". */
const AI_READY_TARGET = 75;

/** Score tiers for the readiness gauge. */
const READINESS_TIERS = [
  { max: 39,  label: 'AI-Invisible',    tone: 'critical' as const, color: SEMANTIC_COLORS.critical },
  { max: 54,  label: 'Falling Behind',  tone: 'warning'  as const, color: SEMANTIC_COLORS.warning  },
  { max: 69,  label: 'Partially Ready', tone: 'warning'  as const, color: SEMANTIC_COLORS.warning  },
  { max: 89,  label: 'AI-Ready',        tone: 'success'  as const, color: SEMANTIC_COLORS.good     },
  { max: 100, label: 'AI-Discoverable', tone: 'success'  as const, color: SEMANTIC_COLORS.excellent },
] as const;

export function BenchmarkPage(): React.ReactElement {
  const { data: benchmark, isLoading, isError, error } = useBenchmarks();
  const { data: optOutData } = useBenchmarkOptOut();
  const updateOptOut = useUpdateBenchmarkOptOut();
  const navigate = useNavigate();

  const isOptedOut = benchmark?.optedOut ?? optOutData?.optedOut ?? false;

  /* ── Helpers ──────────────────────────────────────────────── */

  const getReadinessTier = (score: number) =>
    READINESS_TIERS.find((t) => score <= t.max) ?? READINESS_TIERS.at(-1)!;

  const gapToTarget = (score: number | null) =>
    score == null ? null : Math.max(0, AI_READY_TARGET - score);

  const formatCategoryName = (slug: string): string =>
    slug
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');

  /** Percentage of the sample that scores below the AI-Ready target. */
  const percentBelowTarget = (metrics: { p90Score: number } | null) => {
    if (!metrics) return 97;
    // p90 is the score at the 90th percentile.
    // If even p90 < target, then >90 % of stores are below target.
    if (metrics.p90Score < AI_READY_TARGET) return 97;
    return 85; // conservative fallback
  };

  /* ── Render: Target Gap Bar ──────────────────────────────── */

  const gapBarLabel = (current: number | null, atTarget: boolean): string => {
    if (current == null) return 'N/A';
    if (atTarget) return `${current} ✓`;
    return `${current} / ${AI_READY_TARGET}`;
  };

  const renderGapBar = (label: string, current: number | null) => {
    const gap = gapToTarget(current);
    const atTarget = gap !== null && gap === 0;
    const fillPct = current == null ? 0 : Math.min(100, Math.round((current / AI_READY_TARGET) * 100));
    const fillColor = current == null ? undefined : getReadinessTier(current).color;
    return (
      <Box>
        <InlineStack align="space-between">
          <Text as="span" variant="bodySm">{label}</Text>
          <Text as="span" variant="bodySm" fontWeight="semibold">
            {gapBarLabel(current, atTarget)}
          </Text>
        </InlineStack>
        {current != null && (
          <div
            className="bench-target-bar-track"
            style={{ '--bench-fill-pct': `${fillPct}%`, '--bench-fill-color': fillColor } as React.CSSProperties}
          >
            <div className="bench-target-bar-fill" />
            <div className="bench-target-line" />
          </div>
        )}
      </Box>
    );
  };

  /* ── Render: Coverage Metric ─────────────────────────────── */

  const renderCoverageMetric = (label: string, value: number | null) => (
    <Box>
      <InlineStack align="space-between">
        <Text as="span" variant="bodySm">{label}</Text>
        <Text as="span" variant="bodySm" fontWeight="semibold">
          {value == null ? 'N/A' : `${Math.round(value * 100)}%`}
        </Text>
      </InlineStack>
      {value != null && (
        <ProgressBar progress={Math.round(value * 100)} tone="primary" size="small" />
      )}
    </Box>
  );

  /* ── Render: Your AI Readiness Score ─────────────────────── */

  const renderReadinessScore = () => {
    if (isLoading) {
      return (
        <Card>
          <SkeletonDisplayText size="small" />
          <SkeletonBodyText lines={6} />
        </Card>
      );
    }

    if (isOptedOut) return null;

    if (!benchmark) {
      return (
        <Card>
          <Banner tone="info">
            Benchmark data is not yet available. Your report will be
            generated after your first product scan completes.
          </Banner>
        </Card>
      );
    }

    const score = benchmark.storeScore;
    const gap = gapToTarget(score);
    const tier = score == null ? null : getReadinessTier(score);
    const isReady = gap !== null && gap === 0;

    return (
      <Card>
        <BlockStack gap="400">
          <Text as="h2" variant="headingMd">Your Benchmark Score</Text>

          <InlineStack gap="600" align="start" blockAlign="center">
            {/* Big score number */}
            <BlockStack gap="100" align="center">
              <Text as="span" variant="heading3xl">
                <span className="bench-score-value" style={{ '--bench-score-color': tier?.color ?? 'inherit' } as React.CSSProperties}>{score ?? 'N/A'}</span>
              </Text>
              <Text as="span" variant="bodySm" tone="subdued">
                out of 100
              </Text>
            </BlockStack>

            {/* Readiness badge + gap */}
            <BlockStack gap="200">
              {tier && (
                <Badge tone={tier.tone} size="large">
                  {tier.label}
                </Badge>
              )}
              {gap !== null && gap > 0 && (
                <Text as="span" variant="bodySm" fontWeight="semibold" tone="critical">
                  {gap} points to AI-Ready
                </Text>
              )}
              {isReady && (
                <Text as="span" variant="bodySm" fontWeight="semibold" tone="success">
                  You're ahead of the curve
                </Text>
              )}
            </BlockStack>
          </InlineStack>

          {/* Visual gap bar */}
          {score != null && (
            <Box>
              <div
                className="bench-target-bar-track bench-target-bar-lg"
                style={{ '--bench-fill-pct': `${score}%`, '--bench-fill-color': tier?.color ?? 'inherit', '--bench-line-pos': `${AI_READY_TARGET}%` } as React.CSSProperties}
              >
                <div className="bench-target-bar-fill" />
                <div className="bench-target-line bench-target-line-labeled">
                  <span className="bench-target-label">AI-Ready ({AI_READY_TARGET})</span>
                </div>
              </div>
            </Box>
          )}
        </BlockStack>
      </Card>
    );
  };

  /* ── Render ───────────────────────────────────────────────── */
  return (
    <PageEntrance>
    <Page
      title="Benchmarks"
      subtitle="How prepared is your store for AI-powered product discovery?"
      backAction={{ content: 'Dashboard', url: '/dashboard' }}
    >
      <BlockStack gap="400">
        {/* Hero — urgency narrative */}
        <div className="z-hero">
          <div className="z-hero-content">
            <h2 className="z-hero-title">🚀 AI is reshaping how consumers find products</h2>
            <p className="z-hero-subtitle">
              ChatGPT Shopping, Perplexity, Google AI Overviews — they don't browse, they <em>select</em>.
              Stores that aren't AI-ready are invisible to the next generation of buyers.
            </p>
          </div>
        </div>

      <Layout>
        {isError && (
          <Layout.Section>
            <Banner tone="critical">
              {error?.message ?? 'Failed to load benchmark data.'}
            </Banner>
          </Layout.Section>
        )}

        {/* Opted-Out Banner */}
        {isOptedOut && (
          <Layout.Section>
            <Banner tone="warning">
              You have opted out of benchmark data contribution.
              Benchmarks are two-way — to view your benchmark report,
              your store data must contribute to the aggregate pool.
              Un-check the opt-out below to regain access.
            </Banner>
          </Layout.Section>
        )}

        {/* Your AI Readiness Score */}
        <Layout.Section>
          {renderReadinessScore()}
        </Layout.Section>

        {/* Industry Reality Card */}
        {!isOptedOut && benchmark?.globalBenchmark && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between">
                  <Text as="h2" variant="headingMd">
                    Industry Reality
                  </Text>
                  <Badge tone="warning">
                    {`${percentBelowTarget(benchmark.globalBenchmark)}% of stores are NOT AI-Ready`}
                  </Badge>
                </InlineStack>

                <Banner tone="warning">
                  <BlockStack gap="200">
                    <Text as="span" variant="bodySm" fontWeight="semibold">
                      The industry average benchmark score is just {benchmark.globalBenchmark.avgScore} out of {AI_READY_TARGET} needed.
                    </Text>
                    <Text as="span" variant="bodySm">
                      This means the vast majority of Shopify stores are invisible to AI shopping assistants.{' '}
                      {benchmark.storeScore != null && benchmark.storeScore >= benchmark.globalBenchmark.avgScore
                        ? "You're already ahead — but the AI-Ready bar is higher."
                        : 'The window to get ahead is now, before your competitors wake up.'}
                    </Text>
                  </BlockStack>
                </Banner>

                <BlockStack gap="300">
                  {renderGapBar('Industry Average', benchmark.globalBenchmark.avgScore)}
                  {renderGapBar('Industry Median', benchmark.globalBenchmark.medianScore)}
                  {renderGapBar('Top 10% of stores', benchmark.globalBenchmark.p90Score)}

                  <Divider />
                  <Text as="h3" variant="headingSm">Coverage Gaps</Text>
                  <Text as="span" variant="bodySm" tone="subdued">
                    How well stores provide the structured data AI engines need
                  </Text>
                  {renderCoverageMetric('Product Attribute Coverage', benchmark.globalBenchmark.avgAttributeCoverage)}
                  {renderCoverageMetric('GTIN / Barcode Coverage', benchmark.globalBenchmark.avgGtinCoverage)}
                  {renderCoverageMetric('Schema Markup Coverage', benchmark.globalBenchmark.avgSchemaCoverage)}
                </BlockStack>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* Category Deep-Dive */}
        {!isOptedOut && benchmark?.categoryBenchmark && benchmark.category && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between">
                  <Text as="h2" variant="headingMd">
                    {formatCategoryName(benchmark.category)} — Category Readiness
                  </Text>
                  <Badge tone={
                    benchmark.categoryBenchmark.avgScore >= AI_READY_TARGET ? 'success' : 'warning'
                  }>
                    {`Avg: ${benchmark.categoryBenchmark.avgScore} / ${AI_READY_TARGET}`}
                  </Badge>
                </InlineStack>
                <BlockStack gap="300">
                  {renderGapBar('Category Average', benchmark.categoryBenchmark.avgScore)}
                  {renderGapBar('Category Median', benchmark.categoryBenchmark.medianScore)}
                  {renderGapBar('Category Top 10%', benchmark.categoryBenchmark.p90Score)}
                </BlockStack>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* CTA — push to action */}
        {!isOptedOut && benchmark?.storeScore != null && benchmark.storeScore < AI_READY_TARGET && (
          <Layout.Section>
            <Card>
              <BlockStack gap="300" inlineAlign="center">
                <Text as="h2" variant="headingMd" alignment="center">
                  Close the gap before your competitors do
                </Text>
                <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                  Use AI-Boost to improve your product data and climb toward AI-Ready status.
                </Text>
                <InlineStack align="center" gap="300">
                  <Button variant="primary" onClick={() => navigate('/dashboard')}>
                    View Products to Fix
                  </Button>
                </InlineStack>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* Privacy & Opt-Out */}
        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <Text as="h3" variant="headingSm">
                Privacy & Data
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Industry benchmarks are computed from anonymized, aggregate data.
                No individual store data is ever shared. Only statistical metrics
                (averages, percentiles) are displayed.
              </Text>
              <Divider />
              <Checkbox
                label="Opt out of benchmark data contribution"
                helpText="Two-way: opting out removes your store from aggregate benchmarks AND hides this report."
                checked={optOutData?.optedOut ?? false}
                onChange={(checked) => updateOptOut.mutate(checked)}
              />
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
      </BlockStack>
    </Page>
    </PageEntrance>
  );
}
